import { useEffect, useState } from "react";
import { Widget } from "@typeform/embed-react";
import { createShopifyCustomer } from "../../utils/create-shopify-customer";
import { checkIsValidProvince } from "../../utils/check-is-valid-province";
import { getTypeFormResponse } from "../../utils/get-typeform-response";
import { getCustomerDetails } from "../../utils/get-customer-details";
import { getOptions } from "../../utils/get-options";
import { calculateLoan } from "../../utils/calculate-loan";
import { useAppContext } from "../../context";
import productData from '../../data/product-data.json';
import { updateCustomerWithOrder } from "../../utils/update-customer-with-order";
import { buildQuizMetafields } from "../../utils/quiz-metafields";
import { buildQuoteSummary } from "../../utils/quote-summary";
import { UnknownAnswerError } from "../../utils/answer-labels";
import { trackQuizEvent } from "../../utils/analytics";
import { getBrowserAttribution } from "../../utils/attribution";

// Used if the Shopify section doesn't provide a fallback page.
const DEFAULT_FALLBACK_URL = "/";

export const QuizFormScreen = () => {
  const { setCurrentStep, setShopifyCustomer, setOptions, setTypeformAnswers } =
    useAppContext();

  const [isLoading, setIsLoading] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState(null);
  const [postalCodeFromParams, setPostalCodeFromParams] = useState(null);
  // UTMs / click IDs from this URL or remembered from an earlier page.
  const [attribution] = useState(() => getBrowserAttribution());

  // Province from whichever utility question was answered (for tracking).
  const provinceFromAnswers = (answers = []) => {
    const utility = answers.find((a) => a?.field?.ref?.startsWith("utility_"));
    return utility ? utility.field.ref.split("_")[1].toUpperCase() : undefined;
  };

  // Creates (or finds) the Shopify customer and saves their quiz answers.
  // Returns null when the visitor didn't give full contact details.
  const saveLead = async ({ answersMap, answers, postalCode, outcome, unrecognisedAnswer }) => {
    const customerDetails = getCustomerDetails(answersMap);

    if (
      !customerDetails.firstName ||
      !customerDetails.lastName ||
      !customerDetails.email ||
      !customerDetails.phone
    ) {
      console.warn("Customer details are not complete");
      return null;
    }

    const shopifyCustomer = await createShopifyCustomer({
      ...customerDetails,
      postalCode,
      ...attribution,
    });

    try {
      await updateCustomerWithOrder(
        shopifyCustomer.id,
        {
          metafields: buildQuizMetafields({
            answersMap,
            answers,
            postalCode,
            outcome,
            unrecognisedAnswer,
          }),
        },
        shopifyCustomer.quizToken
      );
    } catch (error) {
      // The lead is already saved; missing answers must not block the visitor.
      console.error("Saving quiz answers failed", error);
    }

    return shopifyCustomer;
  };

  // Visitors we can't price online (renters, 5+ rooms, very large homes,
  // unrecognised answers): save them as a lead with their answers, then show
  // the matching "a specialist will be in touch" screen.
  const finishWithoutQuote = async ({
    answersMap,
    answers,
    postalCode,
    outcome,
    step,
    unrecognisedAnswer,
    fallbackIfNoContact = false,
  }) => {
    let customer = null;
    try {
      customer = await saveLead({ answersMap, answers, postalCode, outcome, unrecognisedAnswer });
    } catch (error) {
      console.error("Saving lead failed", error);
    }

    if (!customer && fallbackIfNoContact) {
      setIsLoading(false);
      window.location.href = fallbackUrl || DEFAULT_FALLBACK_URL;
      return;
    }

    trackQuizEvent("quiz_no_quote", {
      quiz_reason: outcome,
      quiz_province: provinceFromAnswers(answers),
    });

    setCurrentStep(step);
    setTypeformAnswers(answersMap);
    setIsLoading(false);
  };

  // handle typeform submit
  const handleTypeFormSubmit = async (props) => {
    setIsLoading(true);

    const { answers, answersMap } = await getTypeFormResponse(props.responseId);

    // console log the result coming from response
    console.log("Typeform response answers: ", answers);

    trackQuizEvent("quiz_submitted", {
      quiz_province: provinceFromAnswers(answers),
    });

    const postalCode = postalCodeFromParams || answersMap.postal_code_q;

    const isValidProvince = await checkIsValidProvince(postalCode);

    // validate user postal code
    if (!isValidProvince) {
      console.warn("User is outside of service area - invalid province");
      trackQuizEvent("quiz_no_quote", { quiz_reason: "out_of_area" });
      setCurrentStep("outOfServiceArea");
      setTypeformAnswers(answersMap);
      setIsLoading(false);
      return;
    }

    // Redirect if user is outside of service area, only postal code & email in answersMap
    if (Object.keys(answersMap).length === 2 && answersMap.email !== "") {
      trackQuizEvent("quiz_no_quote", { quiz_reason: "out_of_area" });
      setCurrentStep("outOfServiceArea");
      setTypeformAnswers(answersMap);
      setIsLoading(false);
      return;
    }

    // Renters: saved as a lead, shown the renter screen
    if (answersMap.owner === "Rent") {
      await finishWithoutQuote({
        answersMap,
        answers,
        postalCode,
        outcome: "renter",
        step: "customerIsRenter",
      });
      return;
    }

    // 5+ rooms: saved as a lead, a specialist follows up
    if (answersMap.room_number === "5+") {
      await finishWithoutQuote({
        answersMap,
        answers,
        postalCode,
        outcome: "rooms_5_plus",
        step: "customerRoomPlusFive",
      });
      return;
    }

    const customerDetails = getCustomerDetails(answersMap);

    if (
      !customerDetails.firstName ||
      !customerDetails.lastName ||
      !customerDetails.email ||
      !customerDetails.phone
    ) {
      console.warn("Customer details are not complete");
      setIsLoading(false);
      window.location.href = fallbackUrl || DEFAULT_FALLBACK_URL;
      return;
    }

    let options;

    try {
      options = getOptions(answers);
    } catch (error) {
      // Previously these visitors were sent to the "postcode not available"
      // page and no lead was created. Now they are saved and a specialist
      // follows up.
      const isLargeHome = error.message === "Total Btu is too high";
      const unrecognisedAnswer =
        error instanceof UnknownAnswerError
          ? `${error.ref}: ${error.value}`
          : isLargeHome
          ? ""
          : `error: ${error.message}`;
      console.warn("No online quote:", error.message);
      await finishWithoutQuote({
        answersMap,
        answers,
        postalCode,
        outcome: isLargeHome ? "large_home" : "needs_review",
        unrecognisedAnswer,
        step: "customerRoomPlusFive",
        fallbackIfNoContact: true,
      });
      return;
    }

    // No options means the price file had nothing for these answers.
    if (!options || !options.length) {
      await finishWithoutQuote({
        answersMap,
        answers,
        postalCode,
        outcome: "needs_review",
        unrecognisedAnswer: "no options returned",
        step: "customerRoomPlusFive",
        fallbackIfNoContact: true,
      });
      return;
    }

    console.log("Creating Shopify customer with email:", customerDetails.email);
    const shopifyCustomer = await createShopifyCustomer({
      ...customerDetails,
      postalCode,
      ...attribution,
    });
    console.log("Shopify customer created/updated:", shopifyCustomer);

    const optionsWithLoans = await Promise.all(
      options.map(async (option) => {
        // Calculate after-tax total
        const provTaxRate = (option.province && productData.prov_tax_rate[option.province]) || 1.13;
        const subtotal = option.total;
        const tax = subtotal * (provTaxRate - 1);
        const totalWithTax = subtotal + tax;
        const afterIncentive = Math.max(0, totalWithTax - option.grantAllowance);
        const loans = await calculateLoan(afterIncentive);
        return { ...option, loans };
      })
    );

    // Save the quiz answers now, so sales has them even if the visitor
    // leaves without choosing an option.
    try {
      await updateCustomerWithOrder(
        shopifyCustomer.id,
        {
          metafields: buildQuizMetafields({
            answersMap,
            answers,
            postalCode,
            outcome: "quote_shown",
            quoteSummary: buildQuoteSummary(options),
          }),
        },
        shopifyCustomer.quizToken
      );
    } catch (error) {
      console.error("Saving quiz answers failed", error);
    }

    trackQuizEvent("quiz_quote_shown", {
      quiz_province: options[0].province,
      quiz_system_type: options[0].type,
      quiz_option_count: options.length,
    });

    setCurrentStep("quote");
    setShopifyCustomer(shopifyCustomer);
    setOptions(optionsWithLoans);
    setTypeformAnswers(answersMap);
    setIsLoading(false);
  };

  // Where to send visitors the quiz can't quote (missing contact details or
  // a home too large to size automatically). Previously this was disabled,
  // which sent them to a blank URL.
  useEffect(() => {
    const fromSection = document
      .getElementById("root")
      ?.getAttribute("data-fallback-page");

    setFallbackUrl(fromSection || DEFAULT_FALLBACK_URL);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const postalCode = searchParams.get("postal_code");

    if (postalCode) {
      setPostalCodeFromParams(postalCode);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-mt-40">
        <span className="loader !tw-border-blue"></span>
      </div>
    );
  }

  const quizId = process.env.REACT_APP_TYPEFORM_QUIZ_ID;

  return (
    <Widget
      id={quizId}
      style={{ width: "100%", height: "calc(100vh - 100px)" }}
      onSubmit={handleTypeFormSubmit}
      hidden={
        postalCodeFromParams ? { postal_code: postalCodeFromParams } : undefined
      }
      inlineOnMobile={true}
    />
  );
};
